import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiUser, FiAlertCircle } from 'react-icons/fi';
import { getAllMentees } from '../../services/mentorService';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const MenteeList = () => {
  const [mentees, setMentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMentees, setFilteredMentees] = useState([]);

  useEffect(() => {
    const fetchMentees = async () => {
      try {
        setLoading(true);
        const response = await getAllMentees();
        setMentees(response.data);
        setFilteredMentees(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching mentees:', err);
        setError('Failed to load mentees. Please try again later.');
        setLoading(false);
      }
    };

    fetchMentees();
  }, []);

  useEffect(() => {
    const st = String(searchTerm ?? '').trim();
    if (st === '') {
      setFilteredMentees(mentees);
    } else {
      const normalizedSearch = st.toLowerCase();
      const filtered = mentees.filter(
        mentee =>
          (mentee?.full_name || '').toLowerCase().includes(normalizedSearch) ||
          (mentee?.email || '').toLowerCase().includes(normalizedSearch)
      );
      setFilteredMentees(filtered);
    }
  }, [searchTerm, mentees]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by the useEffect
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const copy = pageDescriptions['/mentor/mentees'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <Link
            to="/mentor/dashboard"
            aria-label="Back to mentor dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Back to Dashboard
          </Link>
        }
      />

      {/* Search Bar */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="search"
            name="search"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="Search mentees by name or email"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
      </div>

      {/* Mentees List */}
      <div className="card overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {filteredMentees.length > 0 ? (
            filteredMentees.map((mentee) => (
              <li key={mentee.id}>
                <Link to={`/mentor/mentees/${mentee.id}`} className="block hover:bg-gray-50">
                  <div className="px-6 py-5 sm:px-8">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <FiUser className="h-6 w-6 text-gray-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-600">{mentee.full_name || 'Unknown user'}</div>
                          <div className="text-sm text-gray-500">{mentee.email || 'No email'}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="mr-4 flex flex-col items-end">
                          <div className="text-sm text-gray-900">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${mentee.membership_type === 'gold' ? 'bg-yellow-100 text-yellow-800' : 
                                mentee.membership_type === 'platinum' ? 'bg-green-100 text-green-800' : 
                                'bg-gray-100 text-gray-800'}`}
                            >
                              {mentee.membership_type}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Joined: {(() => {
                              if (!mentee.created_at) return 'Unknown';
                              const joinedDate = new Date(mentee.created_at);
                              return Number.isNaN(joinedDate.getTime()) ? 'Unknown' : joinedDate.toLocaleDateString();
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))
          ) : (
            <li className="px-6 py-5 sm:px-8 text-center text-gray-500">
              {searchTerm ? 'No mentees found matching your search criteria.' : 'You have no mentees yet.'}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default MenteeList;